/**
 * Enhanced Mobile Menu Handler - 2025 Best Practices
 * Features:
 * - Touch/swipe gesture support
 * - Keyboard navigation
 * - ARIA accessibility
 * - Focus trap
 * - Performance optimized
 * - Cross-browser compatible
 */

class EnhancedMobileMenu {
    constructor() {
        // Configuration
        this.config = {
            swipeThreshold: 75,      // Min distance for swipe
            swipeRestraint: 100,     // Max perpendicular distance
            animationDuration: 300,  // Animation time in ms
            breakpoint: 992,         // Mobile breakpoint
            enableSwipe: true,       // Enable swipe gestures
            enableKeyboard: true,    // Enable keyboard navigation
            enableFocusTrap: true    // Enable focus trapping
        };

        // DOM elements
        this.elements = {
            toggle: document.querySelector('.mobile-menu-toggle'),
            nav: document.querySelector('.modern-nav'),
            overlay: document.querySelector('.mobile-menu-overlay'),
            body: document.body,
            header: document.querySelector('.modern-header')
        };

        // State
        this.state = {
            isOpen: false,
            isAnimating: false,
            touchStartX: null,
            touchStartY: null,
            touchEndX: null,
            touchEndY: null
        };

        // Bound methods for event listeners
        this.boundMethods = {
            toggleMenu: this.toggleMenu.bind(this),
            closeMenu: this.closeMenu.bind(this),
            handleKeyDown: this.handleKeyDown.bind(this),
            handleTouchStart: this.handleTouchStart.bind(this),
            handleTouchMove: this.handleTouchMove.bind(this),
            handleTouchEnd: this.handleTouchEnd.bind(this),
            handleResize: this.debounce(this.handleResize.bind(this), 250)
        };

        this.init();
    }

    init() {
        if (!this.elements.toggle || !this.elements.nav) {
            // Mobile menu elements not found
            return;
        }

        this.setupEventListeners();
        this.setupAccessibility();
        this.checkInitialState();
    }

    setupEventListeners() {
        // Toggle button
        this.elements.toggle.addEventListener('click', this.boundMethods.toggleMenu);
        
        // Overlay close
        if (this.elements.overlay) {
            this.elements.overlay.addEventListener('click', this.boundMethods.closeMenu);
        }

        // Keyboard navigation
        if (this.config.enableKeyboard) {
            document.addEventListener('keydown', this.boundMethods.handleKeyDown);
        }

        // Touch/swipe gestures
        if (this.config.enableSwipe && this.isTouchDevice()) {
            this.setupSwipeGestures();
        }

        // Handle window resize
        window.addEventListener('resize', this.boundMethods.handleResize);

        // Close menu on navigation (for SPAs)
        this.setupNavigationHandlers();
    }

    setupSwipeGestures() {
        // Swipe on nav menu itself
        this.elements.nav.addEventListener('touchstart', this.boundMethods.handleTouchStart, { passive: true });
        this.elements.nav.addEventListener('touchmove', this.boundMethods.handleTouchMove, { passive: false });
        this.elements.nav.addEventListener('touchend', this.boundMethods.handleTouchEnd, { passive: true });

        // Swipe from edge to open
        document.addEventListener('touchstart', (e) => {
            const touchX = e.touches[0].clientX;
            const screenWidth = window.innerWidth;
            
            // Detect edge swipe (right 20px of screen)
            if (!this.state.isOpen && touchX > screenWidth - 20) {
                this.state.edgeSwipe = true;
                this.handleTouchStart(e);
            }
        }, { passive: true });

        document.addEventListener('touchmove', (e) => {
            if (this.state.edgeSwipe && !this.state.isOpen) {
                this.handleTouchMove(e);
            }
        }, { passive: false });

        document.addEventListener('touchend', (e) => {
            if (this.state.edgeSwipe) {
                this.state.edgeSwipe = false;
                const swipeDistance = this.state.touchStartX - this.state.touchEndX;
                if (swipeDistance > this.config.swipeThreshold) {
                    this.openMenu();
                }
            }
        }, { passive: true });
    }

    handleTouchStart(e) {
        this.state.touchStartX = e.touches[0].clientX;
        this.state.touchStartY = e.touches[0].clientY;
    }

    handleTouchMove(e) {
        if (!this.state.touchStartX || !this.state.touchStartY) return;

        this.state.touchEndX = e.touches[0].clientX;
        this.state.touchEndY = e.touches[0].clientY;

        // Prevent scrolling when swiping menu
        if (this.state.isOpen) {
            const diffX = this.state.touchStartX - this.state.touchEndX;
            const diffY = Math.abs(this.state.touchStartY - this.state.touchEndY);
            
            // If horizontal swipe is greater than vertical, prevent default
            if (Math.abs(diffX) > diffY) {
                e.preventDefault();
            }
        }
    }

    handleTouchEnd(e) {
        if (!this.state.touchStartX || !this.state.touchEndX) return;

        const diffX = this.state.touchStartX - this.state.touchEndX;
        const diffY = Math.abs(this.state.touchStartY - this.state.touchEndY);

        // Check if swipe is horizontal enough
        if (Math.abs(diffX) > this.config.swipeThreshold && diffY < this.config.swipeRestraint) {
            if (this.state.isOpen && diffX < 0) {
                // Swipe right to close
                this.closeMenu();
            }
        }

        // Reset touch positions
        this.state.touchStartX = null;
        this.state.touchStartY = null;
        this.state.touchEndX = null;
        this.state.touchEndY = null;
    }

    setupAccessibility() {
        // ARIA attributes
        this.elements.toggle.setAttribute('aria-label', 'Toggle navigation menu');
        this.elements.toggle.setAttribute('aria-expanded', 'false');
        this.elements.toggle.setAttribute('aria-controls', 'mobile-nav');
        
        this.elements.nav.setAttribute('aria-hidden', 'true');
        this.elements.nav.setAttribute('role', 'navigation');
        this.elements.nav.setAttribute('aria-label', 'Main navigation');

        if (this.elements.overlay) {
            this.elements.overlay.setAttribute('aria-hidden', 'true');
            this.elements.overlay.setAttribute('role', 'presentation');
        }

        // Ensure touch targets are at least 44x44px
        const navLinks = this.elements.nav.querySelectorAll('a, button');
        navLinks.forEach(link => {
            const styles = window.getComputedStyle(link);
            const height = parseInt(styles.height);
            const padding = parseInt(styles.paddingTop) + parseInt(styles.paddingBottom);
            
            if (height + padding < 44) {
                link.style.minHeight = '44px';
                link.style.display = 'flex';
                link.style.alignItems = 'center';
            }
        });
    }

    setupNavigationHandlers() {
        // Close menu when clicking on nav links
        const navLinks = this.elements.nav.querySelectorAll('a:not([href^="#"])');
        navLinks.forEach(link => {
            link.addEventListener('click', () => {
                // Small delay to allow navigation to start
                setTimeout(() => this.closeMenu(), 100);
            });
        });

        // Handle anchor links
        const anchorLinks = this.elements.nav.querySelectorAll('a[href^="#"]');
        anchorLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                const target = document.querySelector(link.getAttribute('href'));
                if (target) {
                    e.preventDefault();
                    this.closeMenu();
                    setTimeout(() => {
                        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, this.config.animationDuration);
                }
            });
        });
    }

    toggleMenu() {
        if (this.state.isAnimating) return;
        
        if (this.state.isOpen) {
            this.closeMenu();
        } else {
            this.openMenu();
        }
    }

    openMenu() {
        if (this.state.isOpen || this.state.isAnimating) return;

        this.state.isAnimating = true;
        this.state.isOpen = true;

        // Update classes
        this.elements.toggle.classList.add('active');
        this.elements.nav.classList.add('active');
        if (this.elements.overlay) {
            this.elements.overlay.classList.add('active');
        }
        this.elements.body.classList.add('menu-open');

        // Update ARIA
        this.elements.toggle.setAttribute('aria-expanded', 'true');
        this.elements.nav.setAttribute('aria-hidden', 'false');
        if (this.elements.overlay) {
            this.elements.overlay.setAttribute('aria-hidden', 'false');
        }

        // Announce to screen readers
        this.announce('Navigation menu opened');

        // Setup focus trap
        if (this.config.enableFocusTrap) {
            setTimeout(() => this.trapFocus(), 100);
        }

        // Animation complete
        setTimeout(() => {
            this.state.isAnimating = false;
        }, this.config.animationDuration);

        // GSAP animation if available
        this.animateOpen();
    }

    closeMenu() {
        if (!this.state.isOpen || this.state.isAnimating) return;

        this.state.isAnimating = true;
        this.state.isOpen = false;

        // Update classes
        this.elements.toggle.classList.remove('active');
        this.elements.nav.classList.remove('active');
        if (this.elements.overlay) {
            this.elements.overlay.classList.remove('active');
        }
        this.elements.body.classList.remove('menu-open');

        // Update ARIA
        this.elements.toggle.setAttribute('aria-expanded', 'false');
        this.elements.nav.setAttribute('aria-hidden', 'true');
        if (this.elements.overlay) {
            this.elements.overlay.setAttribute('aria-hidden', 'true');
        }

        // Announce to screen readers
        this.announce('Navigation menu closed');

        // Remove focus trap
        if (this.config.enableFocusTrap) {
            this.releaseFocus();
        }

        // Return focus to toggle
        this.elements.toggle.focus();

        // Animation complete
        setTimeout(() => {
            this.state.isAnimating = false;
        }, this.config.animationDuration);
    }

    handleKeyDown(e) {
        // ESC key closes menu
        if (e.key === 'Escape' && this.state.isOpen) {
            e.preventDefault();
            this.closeMenu();
            return;
        }

        // Tab key navigation
        if (e.key === 'Tab' && this.state.isOpen && this.config.enableFocusTrap) {
            this.handleTabKey(e);
        }
    }

    trapFocus() {
        const focusableElements = this.elements.nav.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (focusableElements.length === 0) return;

        this.focusableElements = focusableElements;
        this.firstFocusableElement = focusableElements[0];
        this.lastFocusableElement = focusableElements[focusableElements.length - 1];

        // Focus first element
        this.firstFocusableElement.focus();
    }

    handleTabKey(e) {
        if (!this.firstFocusableElement || !this.lastFocusableElement) return;

        if (e.shiftKey) {
            // Shift + Tab
            if (document.activeElement === this.firstFocusableElement) {
                e.preventDefault();
                this.lastFocusableElement.focus();
            }
        } else {
            // Tab
            if (document.activeElement === this.lastFocusableElement) {
                e.preventDefault();
                this.firstFocusableElement.focus();
            }
        }
    }

    releaseFocus() {
        this.focusableElements = null;
        this.firstFocusableElement = null;
        this.lastFocusableElement = null;
    }

    handleResize() {
        // Close menu if resizing to desktop
        if (window.innerWidth > this.config.breakpoint && this.state.isOpen) {
            this.closeMenu();
        }
    }

    animateOpen() {
        // Use GSAP if available
        if (typeof gsap !== 'undefined') {
            gsap.from(this.elements.nav.querySelectorAll('.nav-link, .btn-nav'), {
                x: 50,
                opacity: 0,
                duration: 0.3,
                stagger: 0.05,
                ease: "power2.out",
                delay: 0.1
            });
        }
    }

    // Utility methods
    isTouchDevice() {
        return ('ontouchstart' in window) || 
               (navigator.maxTouchPoints > 0) || 
               (navigator.msMaxTouchPoints > 0);
    }

    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    announce(message) {
        // Create live region for screen readers
        const announcement = document.createElement('div');
        announcement.setAttribute('role', 'status');
        announcement.setAttribute('aria-live', 'polite');
        announcement.style.position = 'absolute';
        announcement.style.left = '-10000px';
        announcement.textContent = message;
        
        document.body.appendChild(announcement);
        setTimeout(() => {
            document.body.removeChild(announcement);
        }, 1000);
    }

    checkInitialState() {
        // Check if menu should be visible on load
        if (window.innerWidth > this.config.breakpoint) {
            this.elements.nav.style.position = '';
            this.elements.nav.style.right = '';
        }
    }

    // Public API
    destroy() {
        // Remove event listeners
        this.elements.toggle.removeEventListener('click', this.boundMethods.toggleMenu);
        if (this.elements.overlay) {
            this.elements.overlay.removeEventListener('click', this.boundMethods.closeMenu);
        }
        document.removeEventListener('keydown', this.boundMethods.handleKeyDown);
        window.removeEventListener('resize', this.boundMethods.handleResize);
        
        // Clean up touch events
        if (this.config.enableSwipe) {
            this.elements.nav.removeEventListener('touchstart', this.boundMethods.handleTouchStart);
            this.elements.nav.removeEventListener('touchmove', this.boundMethods.handleTouchMove);
            this.elements.nav.removeEventListener('touchend', this.boundMethods.handleTouchEnd);
        }
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.mobileMenu = new EnhancedMobileMenu();
    });
} else {
    window.mobileMenu = new EnhancedMobileMenu();
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
    module.exports = EnhancedMobileMenu;
}